import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValueEvent,
  useReducedMotion,
  useInView,
} from "framer-motion";
import { Eyebrow, Reveal } from "./bits";

const EASE = [0.22, 1, 0.36, 1];
const EMERALD = "#19B77A";

const steps = [
  {
    n: "01",
    title: "Understand",
    desc: "Understand your goals, priorities, current position and what matters most to you.",
    bg: "#DDF4FC",
  },
  {
    n: "02",
    title: "Plan",
    desc: "Build a thoughtful investment and financial strategy around those goals.",
    bg: "#FFF7C7",
  },
  {
    n: "03",
    title: "Grow",
    desc: "Stay invested, review your progress and adapt as life changes.",
    bg: "#DDF7EC",
  },
];

/* Morphing dot-field keyframes: scattered -> organised grid -> upward growth curve */
const SCATTER = [
  [44, 66], [126, 44], [212, 72], [268, 128], [62, 152], [168, 138],
  [248, 204], [44, 246], [132, 222], [220, 282], [92, 322], [258, 336],
];
const GRID = SCATTER.map((_, i) => [56 + (i % 4) * 62, 116 + Math.floor(i / 4) * 86]);
const CURVE = SCATTER.map((_, i) => {
  const t = i / (SCATTER.length - 1);
  return [44 + t * 216, 332 - Math.pow(t, 1.55) * 288];
});
const GRID_ROWS = [116, 202, 288];

const Dot = ({ i, p }) => {
  const cx = useTransform(p, [0, 0.4, 0.6, 1], [SCATTER[i][0], GRID[i][0], GRID[i][0], CURVE[i][0]]);
  const cy = useTransform(p, [0, 0.4, 0.6, 1], [SCATTER[i][1], GRID[i][1], GRID[i][1], CURVE[i][1]]);
  return (
    <motion.circle
      r={i % 4 === 0 ? 6.5 : 4.5}
      fill={i % 5 === 4 ? "#D9A420" : EMERALD}
      opacity={0.9}
      style={{ cx, cy }}
    />
  );
};

const MorphVisual = ({ p, parY }) => {
  const gridLine = useTransform(p, [0.36, 0.46, 0.58, 0.68], [0, 0.45, 0.45, 0]);
  const growthLen = useTransform(p, [0.58, 0.97], [0, 1]);
  const growthOp = useTransform(p, [0.56, 0.64], [0, 1]);
  return (
    <svg
      viewBox="0 0 300 380"
      className="absolute inset-0 h-full w-full"
      fill="none"
      aria-hidden="true"
      data-testid="approach-visual"
    >
      <motion.g style={{ y: parY }}>
        {GRID_ROWS.map((y) => (
          <motion.line
            key={y}
            x1="36"
            x2="272"
            y1={y}
            y2={y}
            stroke="rgba(16,32,46,0.35)"
            strokeDasharray="2 7"
            style={{ opacity: gridLine }}
          />
        ))}
        <motion.path
          d="M44 332 C 110 310, 160 260, 200 170 S 250 70, 260 44"
          stroke={EMERALD}
          strokeWidth="2"
          strokeLinecap="round"
          style={{ pathLength: growthLen, opacity: growthOp }}
        />
        <line x1="36" y1="348" x2="272" y2="348" stroke="rgba(16,32,46,0.14)" />
        {SCATTER.map((_, i) => (
          <Dot key={i} i={i} p={p} />
        ))}
      </motion.g>
    </svg>
  );
};

const PathNode = ({ x, y, i, p }) => {
  const at = i / steps.length;
  const fill = useTransform(p, [Math.max(0, at - 0.005), at + 0.02], ["#B9C2C9", EMERALD]);
  const ring = useTransform(p, [at, at + 0.04], [0, 0.5]);
  return (
    <g>
      <motion.circle cx={x} cy={y} r="12" fill="none" stroke={EMERALD} strokeWidth="1" style={{ opacity: ring }} />
      <motion.circle cx={x} cy={y} r="5.5" style={{ fill }} data-testid={`approach-node-${i + 1}`} />
    </g>
  );
};

const StepText = ({ i, s, p }) => {
  const seg = 1 / steps.length;
  const start = i * seg;
  const end = start + seg;
  const fade = 0.055;
  const inPts = i === 0 ? [0, 0] : [start, start + fade];
  const outPts = i === steps.length - 1 ? [1, 1] : [end - fade, end];
  const pts = [...inPts, ...outPts];
  const vals = i === 0 ? [1, 1, 1, 0] : i === steps.length - 1 ? [0, 1, 1, 1] : [0, 1, 1, 0];
  const opacity = useTransform(p, pts, vals);
  const y = useTransform(p, pts, vals.map((v, k) => (k < 2 ? (v ? 0 : 70) : v ? 0 : -70)));
  const scale = useTransform(p, pts, vals.map((v, k) => (v ? 1 : k < 2 ? 0.95 : 0.97)));
  const pointerEvents = useTransform(opacity, (o) => (o > 0.5 ? "auto" : "none"));
  return (
    <motion.div
      data-testid={`approach-step-${i + 1}`}
      style={{ opacity, y, scale, pointerEvents }}
      className="absolute inset-0 flex flex-col justify-center"
    >
      <span className="font-mono text-xs tracking-[0.3em] text-emerald-600">{s.n}</span>
      <h3 className="mt-4 font-display text-4xl tracking-tight text-[#10202E] sm:text-5xl">{s.title}</h3>
      <p className="mt-5 max-w-md text-base font-light leading-relaxed text-[#4E6A82]">{s.desc}</p>
    </motion.div>
  );
};

const DesktopApproach = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const p = useSpring(scrollYProgress, { stiffness: 80, damping: 24, mass: 0.5 });

  useMotionValueEvent(p, "change", (v) => {
    const i = Math.min(steps.length - 1, Math.floor(Math.min(0.999, Math.max(0, v)) * steps.length));
    setActive((a) => (a === i ? a : i));
  });

  const panelBg = useTransform(p, [0, 0.5, 1], [steps[0].bg, steps[1].bg, steps[2].bg]);
  const parY = useTransform(p, [0, 1], [26, -26]);

  return (
    <section id="approach" ref={ref} data-testid="approach-section" className="relative" style={{ height: "340vh" }}>
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="mx-auto w-full max-w-6xl px-6">
          <Eyebrow>02 — Our Approach</Eyebrow>
          <h2 className="mt-5 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-[3.4rem]">
            A clearer way <span className="italic text-emerald-600">forward.</span>
          </h2>

          <div className="relative mt-12 grid items-center gap-16 lg:grid-cols-[1.05fr_1fr]">
            <svg
              viewBox="0 0 60 600"
              className="absolute -left-24 top-1/2 hidden h-[440px] -translate-y-1/2 xl:block"
              fill="none"
              data-testid="approach-path"
              aria-hidden="true"
            >
              <path
                d="M30 16 C 78 130, -18 250, 30 300 C 78 350, -18 470, 30 584"
                stroke="rgba(16,32,46,0.10)"
                strokeWidth="1.5"
              />
              <motion.path
                d="M30 16 C 78 130, -18 250, 30 300 C 78 350, -18 470, 30 584"
                stroke={EMERALD}
                strokeWidth="1.5"
                style={{ pathLength: p }}
              />
              {[
                [30, 16],
                [30, 300],
                [30, 584],
              ].map(([x, y], i) => (
                <PathNode key={i} x={x} y={y} i={i} p={p} />
              ))}
            </svg>

            <div className="relative h-[240px] sm:h-[230px]">
              {steps.map((s, i) => (
                <StepText key={s.n} i={i} s={s} p={p} />
              ))}
            </div>

            <motion.div
              data-testid="approach-panel"
              style={{ backgroundColor: panelBg }}
              className="relative h-[min(56vh,500px)] overflow-hidden rounded-3xl border border-[#10202E]/[0.08]"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.5),transparent_65%)]" />
              <span className="absolute right-6 top-3 select-none font-display text-[6rem] leading-none text-[#10202E]/[0.06]">
                {steps[active].n}
              </span>
              <MorphVisual p={p} parY={parY} />
              <div className="absolute inset-x-6 bottom-6 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E8698]">
                  {steps[active].title}
                </span>
                <span data-testid="approach-progress" className="font-mono text-[10px] tracking-[0.3em] text-emerald-600/80">
                  {steps[active].n} / 03
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MiniVisual = ({ i }) => (
  <svg viewBox="0 0 72 72" className="h-14 w-14 shrink-0" fill="none" aria-hidden="true">
    {i === 0 && (
      <>
        {[[14, 18], [40, 10], [58, 26], [22, 46], [50, 54]].map(([x, y], k) => (
          <circle key={k} cx={x} cy={y} r={k === 2 ? 4 : 3} fill={k === 4 ? "#D9A420" : EMERALD} opacity={0.85} />
        ))}
      </>
    )}
    {i === 1 && (
      <>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((k) => (
          <circle key={k} cx={14 + (k % 3) * 22} cy={14 + Math.floor(k / 3) * 22} r="3" fill={k === 8 ? "#D9A420" : EMERALD} opacity={0.85} />
        ))}
      </>
    )}
    {i === 2 && (
      <>
        <path d="M10 58 C 26 52, 36 36, 46 26 S 58 12, 62 10" stroke={EMERALD} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="62" cy="10" r="4" fill="#D9A420" />
      </>
    )}
  </svg>
);

const MobileStep = ({ s, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      data-testid={`approach-step-mobile-${i + 1}`}
      initial={{ opacity: 0, y: 40, scale: 0.97 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
      className="relative rounded-3xl border border-[#10202E]/[0.08] p-7"
      style={{ background: s.bg }}
    >
      <motion.span
        data-testid={`approach-node-mobile-${i + 1}`}
        initial={{ backgroundColor: "#B9C2C9", scale: 0.8 }}
        animate={inView ? { backgroundColor: EMERALD, scale: 1 } : {}}
        transition={{ duration: 0.5, ease: EASE }}
        className="absolute -left-[39px] top-9 block h-3 w-3 rounded-full ring-4 ring-[#F8F7F3]"
        aria-hidden="true"
      />
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="font-mono text-xs tracking-[0.3em] text-emerald-700">{s.n}</span>
          <h3 className="mt-2 font-display text-3xl tracking-tight text-[#10202E]">{s.title}</h3>
        </div>
        <MiniVisual i={i} />
      </div>
      <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-[#33506A]">{s.desc}</p>
    </motion.div>
  );
};

const MobileApproach = () => (
  <section id="approach" data-testid="approach-section" className="relative py-24">
    <div className="mx-auto max-w-6xl px-6">
      <Reveal>
        <Eyebrow>02 — Our Approach</Eyebrow>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-6 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl">
          A clearer way <span className="italic text-emerald-600">forward.</span>
        </h2>
      </Reveal>

      <div className="relative mt-14 space-y-6 pl-12">
        <span className="absolute bottom-10 left-[15px] top-10 w-px bg-[#10202E]/10" aria-hidden="true" />
        {steps.map((s, i) => (
          <MobileStep key={s.n} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export const Approach = () => {
  const reduce = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  if (reduce || !isDesktop) return <MobileApproach />;
  return <DesktopApproach />;
};
